import { CreateDashboardPayloadType } from './types';

const sanitizeRectangleData = (data: Record<string, unknown>): Record<string, unknown> => {
  const sanitized = { ...data };

  if ('isLockRatio' in sanitized) {
    const { isLockRatio } = sanitized;

    if (typeof isLockRatio !== 'boolean') {
      throw new Error("'isLockRatio' must be a boolean");
    }

    // No point sending these if lock ratio is set to false.
    if (!isLockRatio) {
      delete sanitized.lockedHeight;
      delete sanitized.lockedWidth;
    }
  }

  if ('strokeThickness' in sanitized) {
    const { strokeThickness } = sanitized;

    if (strokeThickness !== null && typeof strokeThickness !== 'string') {
      throw new Error("'strokeThickness' must be a string or null");
    }

    if (strokeThickness === '') {
      delete sanitized.strokeThickness;
    }
  }

  if ('fillColor' in sanitized) {
    const { fillColor } = sanitized;

    if (fillColor !== null && typeof fillColor !== 'string') {
      throw new Error("'fillColor' must be a string or null");
    }

    if (fillColor === '') {
      delete sanitized.fillColor;
    }
  }

  if ('strokeColor' in sanitized) {
    const { strokeColor } = sanitized;

    if (strokeColor !== null && typeof strokeColor !== 'string') {
      throw new Error("'strokeColor' must be a string or null");
    }

    if (strokeColor === '') {
      delete sanitized.strokeColor;
    }
  }

  return sanitized;
};

const sanitizeAssetData = (data: Record<string, unknown>): Record<string, unknown> => {
  const sanitized = { ...data };

  if ('siteId' in sanitized) {
    const { siteId } = sanitized;

    if (siteId !== null && typeof siteId !== 'string') {
      throw new Error("'siteId' must be a string or null");
    }

    if (siteId === '') {
      delete sanitized.siteId;
    }
  }

  if ('assetId' in sanitized) {
    const { assetId } = sanitized;

    if (assetId !== null && typeof assetId !== 'string') {
      throw new Error("'assetId' must be a string or null");
    }

    if (assetId === '') {
      delete sanitized.assetId;
    }
  }

  return sanitized;
};

const sanitizeTextLabelData = (data: Record<string, unknown>): Record<string, unknown> => {
  const sanitized = { ...data };

  if ('text' in sanitized) {
    const { text } = sanitized;

    if (text !== null && typeof text !== 'string') {
      throw new Error("'text' must be a string or null");
    }

    // TODO: This should be a part of text label validation
    //       A default value is set because if it's an empty string then the component would still exist
    //       on the dashboard but it would be impossivble to select it.
    if (text === '') {
      sanitized.text = 'Label';
    }
  }

  if ('textSize' in sanitized) {
    const { textSize } = sanitized;

    if (textSize !== null && typeof textSize !== 'string') {
      throw new Error("'textSize' must be a string or null");
    }

    if (textSize === '') {
      delete sanitized.textSize;
    }
  }

  if ('backgroundColor' in sanitized) {
    const { backgroundColor } = sanitized;

    if (backgroundColor !== null && typeof backgroundColor !== 'string') {
      throw new Error("'backgroundColor' must be a string or null");
    }

    if (backgroundColor === '') {
      delete sanitized.backgroundColor;
    }
  }

  if ('color' in sanitized) {
    const { color } = sanitized;

    if (color !== null && typeof color !== 'string') {
      throw new Error("'color' must be a string or null");
    }

    if (color === '') {
      delete sanitized.color;
    }
  }

  return sanitized;
};

const sanitizeLiveTagLabelData = (data: Record<string, unknown>): Record<string, unknown> => {
  const sanitized = { ...data };

  if ('siteId' in sanitized) {
    const { siteId } = sanitized;

    if (siteId !== null && typeof siteId !== 'string') {
      throw new Error("'siteId' must be a string or null");
    }

    if (siteId === '') {
      delete sanitized.siteId;
    }
  }

  if ('gatewayId' in sanitized) {
    const { gatewayId } = sanitized;

    if (gatewayId !== null && typeof gatewayId !== 'string') {
      throw new Error("'gatewayId' must be a string or null");
    }

    if (gatewayId === '') {
      delete sanitized.gatewayId;
    }
  }

  if ('tagId' in sanitized) {
    const { tagId } = sanitized;

    if (tagId !== null && typeof tagId !== 'string') {
      throw new Error("'tagId' must be a string or null");
    }

    if (tagId === '') {
      delete sanitized.tagId;
    }
  }

  if ('textSize' in sanitized) {
    const { textSize } = sanitized;

    if (textSize !== null && typeof textSize !== 'string') {
      throw new Error("'textSize' must be a string or null");
    }

    if (textSize === '') {
      delete sanitized.textSize;
    }
  }

  return sanitized;
};

const sanitizeImageUploadData = (data: Record<string, unknown>): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = {};

  if ('label' in data) {
    sanitized.label = data.label;
  }

  if ('drawerType' in data) {
    sanitized.drawerType = data.drawerType;
  }

  if ('objectKey' in data && data.objectKey) {
    sanitized.url = data.objectKey;
  }

  // When saving dashboard for existing image get
  // objectKey from presigned GET url from backend
  if ('url' in data && data.url) {
    sanitized.url = new URL(data.url).pathname.substring(1);
  }

  return sanitized;
};

const sanitizeDashboardPayload = (
  payload: CreateDashboardPayloadType
): CreateDashboardPayloadType => {
  const { title } = payload;

  const nodes = payload.nodes.map((node) => {
    const newNode = { ...node };

    if (!('drawerType' in node.data)) {
      throw new Error("Node is missing required 'drawerType' attribute");
    }

    const { drawerType } = node.data;

    if (typeof drawerType !== 'string') {
      throw new Error("'drawerType' must be a string");
    }

    switch (drawerType) {
      case 'rectangle': {
        newNode.data = sanitizeRectangleData(newNode.data);
        break;
      }
      case 'asset': {
        newNode.data = sanitizeAssetData(newNode.data);
        break;
      }
      case 'textLabel': {
        newNode.data = sanitizeTextLabelData(newNode.data);
        break;
      }
      case 'liveTagLabel': {
        newNode.data = sanitizeLiveTagLabelData(newNode.data);
        break;
      }
      case 'imageUpload': {
        newNode.data = sanitizeImageUploadData(newNode.data);
        break;
      }
    }

    return newNode;
  });

  return {
    title: title,
    nodes: nodes,
  };
};

export { sanitizeDashboardPayload };
